import * as React from "react";
import { isNodeSelection, type Editor } from "@tiptap/react";

import { useMenuNavigation } from "containers/Tiptap/hooks/use-menu-navigation";

import { useTiptapEditor } from "containers/Tiptap/hooks/use-tiptap-editor";

import { BanIcon } from "containers/Tiptap/components/tiptap-icons/ban-icon";
import { HighlighterIcon } from "containers/Tiptap/components/tiptap-icons/highlighter-icon";

import { isMarkInSchema } from "containers/Tiptap/lib/tiptap-utils";

import type { ButtonProps } from "containers/Tiptap/components/tiptap-ui-primitive/button";
import { Button } from "containers/Tiptap/components/tiptap-ui-primitive/button";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "containers/Tiptap/components/tiptap-ui-primitive/popover";

import {
  ColorHighlightButton,
  canToggleHighlight,
} from "containers/Tiptap/components/tiptap-ui/color-highlight-button";

import "containers/Tiptap/components/tiptap-ui/color-highlight-button/color-highlight-button.scss";
import { Separator } from "containers/Tiptap/components/tiptap-ui-primitive/separator/separator";
import { colors as themeColors } from "theme/colors";

export interface ColorHighlightPopoverColor {
  label: string;
  value: string;
  border?: string;
}

export interface ColorHighlightPopoverContentProps {
  editor?: Editor | null;
  colors?: ColorHighlightPopoverColor[];
  onClose?: () => void;
}

export interface ColorHighlightPopoverProps extends Omit<ButtonProps, "type"> {
  editor?: Editor | null;
  colors?: ColorHighlightPopoverColor[];
  hideWhenUnavailable?: boolean;
}

export const DEFAULT_HIGHLIGHT_COLORS: ColorHighlightPopoverColor[] = [
  {
    label: "Green",
    value: "#4CAF50",
    border: "#c7fad8",
  },
  {
    label: "Blue",
    value: themeColors.primary.main,
    border: "#ceeafd",
  },
  {
    label: "Red",
    value: "#FF0000",
    border: "#ffccd0",
  },
  {
    label: "Purple",
    value: "#7E57C2",
    border: "#e4ccff",
  },
  {
    label: "Orange",
    value: "#FF9800",
    border: "#ffe0b3",
  },
];

export const ColorHighlightPopoverButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, children, ...props }, ref) => (
    <Button
      type="button"
      className={className}
      data-style="ghost"
      data-appearance="default"
      role="button"
      tabIndex={-1}
      aria-label="Highlight text"
      tooltip="Highlight"
      ref={ref}
      {...props}
    >
      {children || <HighlighterIcon className="tiptap-button-icon" />}
    </Button>
  ),
);

ColorHighlightPopoverButton.displayName = "ColorHighlightPopoverButton";

export function ColorHighlightPopoverContent({
  editor: providedEditor,
  colors = DEFAULT_HIGHLIGHT_COLORS,
  onClose,
}: ColorHighlightPopoverContentProps): React.ReactNode {
  const { editor } = useTiptapEditor(providedEditor);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const removeHighlight = React.useCallback(() => {
    if (!editor) return;
    editor.chain().focus().unsetMark("highlight").run();
    onClose?.();
  }, [editor, onClose]);

  const menuItems = React.useMemo(
    () => [...colors, { label: "Remove highlight", value: "none" }],
    [colors],
  );

  const { selectedIndex } = useMenuNavigation({
    containerRef,
    items: menuItems,
    orientation: "both",
    onSelect: (item) => {
      if (item.value === "none") {
        removeHighlight();
      }
      onClose?.();
    },
    onClose,
    autoSelectFirstItem: false,
  });

  return (
    <div ref={containerRef} className="tiptap-color-highlight-content" tabIndex={0} role="menu">
      <div className="tiptap-button-group" data-orientation="horizontal">
        {colors.map((color, index) => (
          <ColorHighlightButton
            key={color.value}
            editor={editor}
            color={color.value}
            aria-label={`${color.label} highlight colour`}
            tooltip={color.label}
            tabIndex={index === selectedIndex ? 0 : -1}
            data-highlighted={selectedIndex === index}
            onClick={onClose}
          />
        ))}
      </div>
      <Separator />
      <div className="tiptap-button-group">
        <Button
          onClick={removeHighlight}
          aria-label="Remove highlight"
          tooltip="Remove highlight"
          tabIndex={selectedIndex === colors.length ? 0 : -1}
          type="button"
          role="menuitem"
          data-style="ghost"
          data-highlighted={selectedIndex === colors.length}
        >
          <BanIcon className="tiptap-button-icon" />
        </Button>
      </div>
    </div>
  );
}

export function ColorHighlightPopover({
  editor: providedEditor,
  colors = DEFAULT_HIGHLIGHT_COLORS,
  hideWhenUnavailable = false,
  ...props
}: ColorHighlightPopoverProps): React.ReactNode {
  const { editor } = useTiptapEditor(providedEditor);
  const [isOpen, setIsOpen] = React.useState(false);
  const [isDisabled, setIsDisabled] = React.useState(false);

  const markAvailable = isMarkInSchema("highlight", editor);

  React.useEffect(() => {
    if (!editor) return;

    const updateIsDisabled = (): void => {
      let disabled = false;

      if (!markAvailable || !editor) {
        disabled = true;
      }

      const isInCompatibleContext =
        editor.isActive("codeBlock") || editor.isActive("imageUpload");

      if (isInCompatibleContext) {
        disabled = true;
      }

      setIsDisabled(disabled);
    };

    editor.on("selectionUpdate", updateIsDisabled);
    editor.on("update", updateIsDisabled);

    return () => {
      editor.off("selectionUpdate", updateIsDisabled);
      editor.off("update", updateIsDisabled);
    };
  }, [editor, markAvailable]);

  const isActive = editor?.isActive("highlight") ?? false;

  const shouldShow = React.useMemo(() => {
    if (!hideWhenUnavailable || !editor) return true;

    return !(isNodeSelection(editor.state.selection) || !canToggleHighlight(editor));
  }, [hideWhenUnavailable, editor]);

  if (!shouldShow || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <ColorHighlightPopoverButton
          disabled={isDisabled}
          data-active-state={isActive ? "on" : "off"}
          data-disabled={isDisabled}
          aria-pressed={isActive}
          {...props}
        />
      </PopoverTrigger>

      <PopoverContent side="top" align="center" sideOffset={14} aria-label="Highlight colours">
        <ColorHighlightPopoverContent
          editor={editor}
          colors={colors}
          onClose={() => setIsOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

export default ColorHighlightPopover;
